import React, { useState, useEffect, useMemo } from 'react';

/**
 * Gráfico de Barras por Categoria
 * @param {Array} transactions - Lista de transações
 * @param {string} type - Tipo: 'income' ou 'expense'
 */
const CategoryBarChart = React.memo(({ transactions, type, totalGross, onCategoryClick }) => {
    const [categoryTotals, setCategoryTotals] = useState([]);
    const [maxValue, setMaxValue] = useState(0);

    useEffect(() => {
        const filtered = transactions.filter(t => t.type === type);
        const totals = filtered.reduce((acc, curr) => {
            const category = curr.category || 'Outros';
            if (!acc[category]) {
                acc[category] = 0;
            }
            acc[category] += curr.amount;
            return acc;
        }, {});

        const sortedTotals = Object.entries(totals)
            .map(([category, total]) => ({ category, total }))
            .sort((a, b) => b.total - a.total);

        setCategoryTotals(sortedTotals);
        setMaxValue(sortedTotals.length > 0 ? sortedTotals[0].total : 0);

    }, [transactions, type]);

    const formatCurrency = useMemo(() => {
        return (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    }, []);

    const totalPeriod = useMemo(() => {
        return categoryTotals.reduce((acc, curr) => acc + curr.total, 0);
    }, [categoryTotals]);

    if (categoryTotals.length === 0) {
        return <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">Sem dados de {type === 'income' ? 'ganhos' : 'custos'} para este período.</p>;
    }

    return (
        <div className="space-y-3 pt-3">
            {categoryTotals.map(({ category, total }) => {
                const widthPercent = maxValue > 0 ? (total / maxValue) * 100 : 0;

                // Se for despesa e tivermos o valor bruto, calculamos sobre o bruto.
                // Caso contrário (ou se for receita), usamos o total do período (total do grupo).
                const baseValue = (type === 'expense' && totalGross > 0) ? totalGross : totalPeriod;
                const sharePercent = baseValue > 0 ? (total / baseValue) * 100 : 0;
                const barColor = type === 'income'
                    ? 'bg-green-500 dark:bg-green-400'
                    : 'bg-red-500 dark:bg-red-400';

                return (
                    <div 
                        key={category} 
                        className={`w-full ${onCategoryClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                        onClick={() => onCategoryClick && onCategoryClick(category)}
                    >
                        <div className="flex justify-between items-center text-xs mb-1">
                            <span className="font-medium text-gray-700 dark:text-gray-300 truncate pr-2">{category}</span>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500">{sharePercent.toFixed(1)}%</span>
                                <span className="font-bold text-gray-800 dark:text-gray-100">{formatCurrency(total)}</span>
                            </div>
                        </div>
                        <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full ${barColor} transition-all duration-500 ease-out`}
                                style={{ width: `${widthPercent}%` }}
                                title={`${category}: ${formatCurrency(total)} (${sharePercent.toFixed(1)}%)`}
                            ></div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

CategoryBarChart.displayName = 'CategoryBarChart';

export default CategoryBarChart;
